import { useSyncExternalStore } from 'react';

// Per-app EPUB reader features. Admin turns them on at startup; teacher and
// student turn them on per school (feature gate), everyone else keeps the safe
// defaults (all off).
export interface EpubReaderFeatures {
  // Runs book JavaScript (interactive EPUB). epub.js then sandboxes the book with
  // allow-same-origin + allow-scripts, so its scripts run as our app in the
  // viewer's session. Only enable where every book shown is trusted.
  scripts: boolean;
  // Links in the book may open new windows (sandbox allow-popups).
  popups: boolean;
  // Search box that finds text across the book.
  search: boolean;
  // Font size and light/dark controls.
  displayControls: boolean;
}

let features: EpubReaderFeatures = {
  scripts: false,
  popups: false,
  search: false,
  displayControls: false,
};

const listeners = new Set<() => void>();

export function setEpubReaderFeatures(next: Partial<EpubReaderFeatures>) {
  const merged = { ...features, ...next };
  const changed = (Object.keys(merged) as (keyof EpubReaderFeatures)[]).some(
    (key) => merged[key] !== features[key]
  );
  if (!changed) return;
  features = merged;
  listeners.forEach((listener) => listener());
}

export function getEpubReaderFeatures(): EpubReaderFeatures {
  return features;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Re-renders the reader when the features change (e.g. school gate resolved).
export function useEpubReaderFeatures(): EpubReaderFeatures {
  return useSyncExternalStore(subscribe, getEpubReaderFeatures);
}
